import { useEffect, useState } from "react"
import api from "../../api/axios"
import {
  Users,
  DollarSign,
  CalendarCheck,
  UserPlus,
  TrendingUp,
  ArrowRight,
} from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { StatsCard } from "../../components/ui/StatsCard"
import { Badge } from "../../components/ui/Badge"
import { Avatar } from "../../components/ui/Avatar"
import { Button } from "../../components/ui/Button"
import { CardSkeleton } from "../../components/ui/Skeleton"
import { EmptyState } from "../../components/ui/EmptyState"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js"
import { Bar, Line } from "react-chartjs-2"
import { useNavigate } from "react-router-dom"
import { fr } from "../../locales/fr"
import { accessStatusLabel } from "../../locales/fr"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

export default function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    members: 0,
    activeSubscriptions: 0,
    revenueToday: 0,
    entriesToday: 0,
  })
  const [revenue, setRevenue] = useState([])
  const [members, setMembers] = useState([])
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    load()
  }, [])

  const load = async () => {
    try {
      const res = await api.get("/dashboard")
      setStats(res.data.stats || {})
      setRevenue(res.data.revenue || [])
      setMembers(res.data.lastMembers || [])
      setLogs(res.data.lastAccess || [])
    } catch (e) {
      console.error("Dashboard error", e)
    }
    setLoading(false)
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "hsl(0 0% 9%)",
        titleColor: "hsl(0 0% 98%)",
        bodyColor: "hsl(0 0% 98%)",
        borderColor: "hsl(0 0% 18%)",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: {
          color: "hsl(0 0% 18%)",
          drawBorder: false,
        },
        ticks: {
          color: "hsl(0 0% 64%)",
        },
      },
      y: {
        grid: {
          color: "hsl(0 0% 18%)",
          drawBorder: false,
        },
        ticks: {
          color: "hsl(0 0% 64%)",
        },
      },
    },
  }

  const revenueChartData = {
    labels: revenue.map((r) => r.month),
    datasets: [
      {
        label: fr.dashboard.charts.revenueLabel,
        data: revenue.map((r) => r.amount),
        backgroundColor: "hsl(262 83% 58% / 0.8)",
        borderColor: "hsl(262 83% 58%)",
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  }

  const memberGrowthData = {
    labels: revenue.map((r) => r.month),
    datasets: [
      {
        label: fr.dashboard.charts.membersLabel,
        data: revenue.map((_, i) => Math.floor(Math.random() * 50) + 20 + i * 5),
        fill: true,
        backgroundColor: "hsl(142 76% 36% / 0.1)",
        borderColor: "hsl(142 76% 36%)",
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "hsl(142 76% 36%)",
      },
    ],
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{fr.dashboard.title}</h1>
          <p className="text-muted-foreground">{fr.dashboard.subtitle}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{fr.dashboard.chartLoading}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{fr.dashboard.chartLoading}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{fr.dashboard.title}</h1>
        <p className="text-muted-foreground">{fr.dashboard.subtitle}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title={fr.dashboard.stats.totalMembers}
          value={stats.members}
          change={fr.dashboard.stats.changeFromLastMonth}
          changeType="positive"
          icon={Users}
        />
        <StatsCard
          title={fr.dashboard.stats.monthlyRevenue}
          value={`${stats.revenueToday} MAD`}
          change={fr.dashboard.stats.changeFromLastMonth}
          changeType="positive"
          icon={DollarSign}
        />
        <StatsCard
          title={fr.dashboard.stats.activeSubscriptions}
          value={stats.activeSubscriptions}
          change={fr.dashboard.stats.changeFromLastMonth}
          changeType="positive"
          icon={CalendarCheck}
        />
        <StatsCard
          title={fr.dashboard.stats.entriesToday}
          value={stats.entriesToday}
          change={fr.dashboard.stats.moreThanYesterday}
          changeType="positive"
          icon={UserPlus}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{fr.dashboard.charts.revenueTitle}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">{fr.dashboard.charts.revenueHint}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-success">
              <TrendingUp className="h-4 w-4" />
              +12,5 %
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {revenue.length > 0 ? (
                <Bar data={revenueChartData} options={chartOptions} />
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  {fr.dashboard.charts.noRevenue}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{fr.dashboard.charts.growthTitle}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">{fr.dashboard.charts.growthHint}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-success">
              <TrendingUp className="h-4 w-4" />
              +8,2 %
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {revenue.length > 0 ? (
                <Line data={memberGrowthData} options={chartOptions} />
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  {fr.dashboard.charts.noMembers}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{fr.dashboard.recentMembers}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/members")}>
              {fr.common.viewAll}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <EmptyState
                icon={Users}
                title={fr.dashboard.emptyMembersTitle}
                description={fr.dashboard.emptyMembersDesc}
                action={() => navigate("/members")}
                actionLabel={fr.common.addMember}
              />
            ) : (
              <div className="space-y-4">
                {members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar fallback={m.name?.charAt(0)} alt={m.name} size="md" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{m.name}</p>
                        <p className="text-xs text-muted-foreground">{m.email || fr.common.noEmail}</p>
                      </div>
                    </div>
                    <Badge variant="success">{fr.status.active}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{fr.dashboard.recentActivity}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/logs")}>
              {fr.common.viewAll}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <EmptyState
                icon={CalendarCheck}
                title={fr.dashboard.emptyActivityTitle}
                description={fr.dashboard.emptyActivityDesc}
              />
            ) : (
              <div className="space-y-4">
                {logs.map((l) => (
                  <div
                    key={l.id}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-2 w-2 rounded-full ${
                          l.status === "granted" ? "bg-success" : "bg-destructive"
                        }`}
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground">{l.member}</p>
                        <p className="text-xs text-muted-foreground">{l.time || fr.common.justNow}</p>
                      </div>
                    </div>
                    <Badge variant={l.status === "granted" ? "success" : "destructive"}>
                      {accessStatusLabel(l.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
